import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, PencilIcon, PlusIcon, TagsIcon, Trash2Icon } from 'lucide-react';
import { Button, ConfirmDialog, DataTable, EmptyState, ErrorNotice, Field, Panel, SearchInput, SelectInput, SkeletonTable, TextInput, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  ASSISTANT_ROLES,
  ASSISTANT_ROLE_LABELS,
  OFFSET_EDGE,
  PAY_BASIS,
  PAY_BASIS_LABELS,
  createAssistantCategory,
  deleteAssistantCategory,
  describeOffset,
  formatPay,
  listAssistantCategories,
  updateAssistantCategory,
  type AssistantCategory,
  type AssistantCategoryWrite,
  type AssistantRole,
  type PayBasis,
} from '@/lib/staffing';

const SORT_FIELDS = { name: 'name', role: 'roleLabel', pay: 'payRate' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.name, direction: 'asc' };
const SEARCH_KEY = 'categoriesQ';
const QUERY_KEY = ['assistant-categories'] as const;

/** The longest offset the API takes: a day either side. */
const MAX_OFFSET_MINUTES = 24 * 60;

interface CategoryRow extends AssistantCategory {
  readonly roleLabel: string;
  readonly payLabel: string;
  readonly startLabel: string;
  readonly endLabel: string;
}

function categoryRow(category: AssistantCategory): CategoryRow {
  return {
    ...category,
    roleLabel: ASSISTANT_ROLE_LABELS[category.role],
    payLabel: formatPay(category.payRate, category.payBasis) ?? '',
    startLabel: describeOffset(category.callOffsetMinutes, OFFSET_EDGE.start) ?? '',
    endLabel: describeOffset(category.endOffsetMinutes, OFFSET_EDGE.end) ?? '',
  };
}

const SEARCH_FIELDS: ReadonlyArray<keyof CategoryRow> = ['name', 'roleLabel', 'payLabel'];

/**
 * Settings → Assistant categories: the standard role, pay and hours for a
 * group of assistants ("Lead Assistant", "Assistant"). Picking one on an
 * event's assistant fills those in; each stays editable per person. Deleting
 * one keeps its name on the hires that already carry it.
 *
 * @returns The tab.
 */
export function AssistantCategoriesTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const { search, setSearch } = useUrlSearch(SEARCH_KEY);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AssistantCategory | null>(null);
  const [deleting, setDeleting] = useState<AssistantCategory | null>(null);
  const categories = useQuery({ queryKey: QUERY_KEY, queryFn: ({ signal }) => listAssistantCategories(signal) });

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }
  const remove = useMutation({
    mutationFn: (category: AssistantCategory) => deleteAssistantCategory(category._id),
    onSuccess: async (_result, category) => {
      toast.success(`Deleted ${category.name}`);
      setDeleting(null);
      await refresh();
    },
  });

  const all = categories.data ?? [];
  const rows = sortRows(filterRows(all.map(categoryRow), search, SEARCH_FIELDS), sort);
  const addButton = (
    <Button onClick={() => setAdding(true)}>
      <PlusIcon aria-hidden className="size-4" /> Add category
    </Button>
  );

  return (
    <div className="mt-6 space-y-4">
      {all.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput label="Search assistant categories" placeholder="Name, role or pay" value={search} onChange={(event) => setSearch(event.target.value)} className="max-w-xs" />
          <span data-guide="assistant-categories-add" className="ml-auto">
            {addButton}
          </span>
        </div>
      ) : null}

      {categories.isPending ? (
        <SkeletonTable label="Loading assistant categories…" />
      ) : categories.isError ? (
        <ErrorNotice title="Assistant categories unavailable" message={errorMessage(categories.error)} />
      ) : all.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<TagsIcon aria-hidden className="size-6" />}
            title="No assistant categories yet"
            message="A category holds the standard role, pay and hours for a group of assistants, such as Lead Assistant at $30 an hour starting an hour before the event. Pick it on an event to fill those in, or use it to add a whole team at once."
            action={addButton}
          />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState title="No categories match" message="Try a different search." />
        </Panel>
      ) : (
        <DataTable
          guide="assistant-categories-table"
          headers={[{ label: 'Category', sortKey: SORT_FIELDS.name }, { label: 'Role', sortKey: SORT_FIELDS.role }, { label: 'Pay', sortKey: SORT_FIELDS.pay }, 'Hours', 'Actions']}
          visuallyHiddenHeaders={[4]}
          sort={sort}
          onSort={onSort}
          cards={rows.map((row) => ({
            key: row._id,
            title: row.name,
            subtitle: row.roleLabel,
            fields: [
              { label: 'Pay', value: row.payLabel || 'Not set' },
              { label: 'Starts', value: row.startLabel || 'Not set' },
              { label: 'Ends', value: row.endLabel || 'Not set' },
            ],
            action: <CategoryMenu category={row} onEdit={setEditing} onDelete={setDeleting} />,
          }))}
        >
          {rows.map((row) => (
            <tr key={row._id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small font-bold">{row.name}</td>
              <td className="p-3 text-portal-small text-portal-muted">{row.roleLabel}</td>
              <td className="p-3 text-portal-small whitespace-nowrap tabular-nums">{row.payLabel || <span className="text-portal-muted">Not set</span>}</td>
              <td className="p-3 text-portal-small">
                <span className="block">{row.startLabel || 'Start not set'}</span>
                <span className="block text-portal-micro text-portal-muted">{row.endLabel || 'End not set'}</span>
              </td>
              <td className="p-3 text-right">
                <CategoryMenu category={row} onEdit={setEditing} onDelete={setDeleting} />
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      {adding ? (
        <CategoryDialog
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      ) : null}
      {editing ? (
        <CategoryDialog
          key={editing._id}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        tone="danger"
        title={`Delete ${deleting?.name ?? 'this category'}?`}
        message="It stops being offered on events. Assistants already in it keep their pay, hours and the category name."
        confirmLabel="Delete"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
      />
    </div>
  );
}

function CategoryMenu({ category, onEdit, onDelete }: { readonly category: AssistantCategory; readonly onEdit: (category: AssistantCategory) => void; readonly onDelete: (category: AssistantCategory) => void }): React.ReactElement {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Actions for ${category.name}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
          <EllipsisIcon aria-hidden className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onEdit(category)}>
          <PencilIcon aria-hidden className="size-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => onDelete(category)}>
          <Trash2Icon aria-hidden className="size-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "" stays empty; anything else is a whole number of minutes, or NaN when it is not one. */
function minutesFrom(text: string): number | null {
  return text.trim() === '' ? null : Number(text);
}

function validMinutes(value: number | null): boolean {
  return value === null || (Number.isInteger(value) && value >= 0 && value <= MAX_OFFSET_MINUTES);
}

/** Adds or edits one category. */
function CategoryDialog({ existing, onClose, onSaved }: { readonly existing?: AssistantCategory; readonly onClose: () => void; readonly onSaved: () => Promise<void> }): React.ReactElement {
  const [name, setName] = useState(existing?.name ?? '');
  const [role, setRole] = useState<AssistantRole>(existing?.role ?? 'assistant');
  const [payRate, setPayRate] = useState(existing?.payRate === null || existing?.payRate === undefined ? '' : String(existing.payRate));
  const [payBasis, setPayBasis] = useState<PayBasis>(existing?.payBasis ?? PAY_BASIS.hour);
  const [callOffset, setCallOffset] = useState(existing?.callOffsetMinutes === null || existing?.callOffsetMinutes === undefined ? '' : String(existing.callOffsetMinutes));
  const [endOffset, setEndOffset] = useState(existing?.endOffsetMinutes === null || existing?.endOffsetMinutes === undefined ? '' : String(existing.endOffsetMinutes));

  const rate = payRate.trim() === '' ? null : Number(payRate);
  const call = minutesFrom(callOffset);
  const end = minutesFrom(endOffset);
  const body: AssistantCategoryWrite = {
    name: name.trim(),
    role,
    payRate: rate,
    payBasis: rate === null ? null : payBasis,
    callOffsetMinutes: call,
    endOffsetMinutes: end,
  };
  const save = useMutation({
    mutationFn: () => (existing ? updateAssistantCategory(existing._id, body) : createAssistantCategory(body)),
    onSuccess: async (saved) => {
      toast.success(existing ? `Saved ${saved.name}` : `Added ${saved.name}`);
      await onSaved();
    },
  });
  const valid = body.name.length > 0 && (rate === null || (Number.isFinite(rate) && rate >= 0)) && validMinutes(call) && validMinutes(end);

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-lg">
        <DialogTitle>{existing ? 'Edit assistant category' : 'Add an assistant category'}</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !save.isPending) save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field guide="assistant-category-name" label="Name" htmlFor="category-name" required hint="As it reads on the event, such as Lead Assistant.">
              <TextInput id="category-name" value={name} maxLength={60} placeholder="Lead Assistant" autoFocus onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field label="Role" htmlFor="category-role" required hint="What they do on the day.">
              <SelectInput id="category-role" value={role} onChange={(event) => setRole(event.target.value as AssistantRole)}>
                {ASSISTANT_ROLES.map((key) => (
                  <option key={key} value={key}>
                    {ASSISTANT_ROLE_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field guide="assistant-category-pay" label="Pay" htmlFor="category-pay" hint="Dollars. Leave empty to set it per person.">
              <TextInput id="category-pay" type="number" min={0} step="0.01" value={payRate} placeholder="30" onChange={(event) => setPayRate(event.target.value)} />
            </Field>
            <Field label="Pay basis" htmlFor="category-pay-basis" hint="Per hour counts the hours between start and end.">
              <SelectInput id="category-pay-basis" value={payBasis} disabled={rate === null} onChange={(event) => setPayBasis(event.target.value as PayBasis)}>
                {Object.values(PAY_BASIS).map((key) => (
                  <option key={key} value={key}>
                    {PAY_BASIS_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field guide="assistant-category-start" label="Start, minutes before the event" htmlFor="category-call" hint="60 starts them an hour early; 0 at the start. Empty leaves it to each person.">
              <TextInput id="category-call" type="number" min={0} max={MAX_OFFSET_MINUTES} step={5} value={callOffset} placeholder="60" onChange={(event) => setCallOffset(event.target.value)} />
            </Field>
            <Field label="End, minutes after the event" htmlFor="category-end" hint="30 keeps them half an hour to pack up; 0 at the end.">
              <TextInput id="category-end" type="number" min={0} max={MAX_OFFSET_MINUTES} step={5} value={endOffset} placeholder="30" onChange={(event) => setEndOffset(event.target.value)} />
            </Field>
          </div>
          <p className="text-portal-micro text-portal-muted">
            Times are counted from each event&rsquo;s own start and end. When an event has no time yet, the assistant&rsquo;s times stay empty to fill in. Pay is internal and never shown to facilitators.
          </p>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add category'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
